import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import {
    FileText,
    RefreshCw,
    Plus,
    Search,
    X,
    Pencil,
    Trash2,
    XCircle,
    AlertTriangle,
} from "lucide-react";

const API_PREFIX = "/brochure";

export default function Brochure() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    const [query, setQuery] = useState("");

    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return items;
        return items.filter((b) => {
            const hay = `${b.topic || ""} ${b.link || ""} ${b.description || ""}`.toLowerCase();
            return hay.includes(q);
        });
    }, [items, query]);

    async function fetchBrochures() {
        try {
            setLoading(true);
            const res = await axiosInstance.get(`${API_PREFIX}/`);
            setItems(Array.isArray(res.data) ? res.data : []);
        } catch {
            toast.error("Failed to load brochures.");
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchBrochures();
    }, []);

    function openCreate() {
        setEditing(null);
        setIsFormOpen(true);
    }

    function openEdit(item) {
        setEditing(item);
        setIsFormOpen(true);
    }

    function openDelete(item) {
        setDeleting(item);
        setIsDeleteOpen(true);
    }

    async function handleDeleteConfirm() {
        if (!deleting?.brochure_id) return;
        const id = deleting.brochure_id;

        try {
            setBusyId(id);
            await axiosInstance.delete(`${API_PREFIX}/${id}`);
            setItems((prev) => prev.filter((x) => x.brochure_id !== id));
            toast.success("Brochure deleted.");
            setIsDeleteOpen(false);
            setDeleting(null);
        } catch {
            toast.error("Failed to delete brochure.");
        } finally {
            setBusyId(null);
        }
    }

    async function handleSave(payload) {
        try {
            setBusyId(payload?.brochure_id || "new");

            if (payload?.brochure_id) {
                const id = payload.brochure_id;
                const res = await axiosInstance.put(`${API_PREFIX}/${id}`, {
                    topic: payload.topic,
                    link: payload.link,
                    description: payload.description,
                });

                setItems((prev) => prev.map((x) => (x.brochure_id === id ? res.data : x)));
                toast.success("Brochure updated.");
            } else {
                const res = await axiosInstance.post(`${API_PREFIX}/`, {
                    topic: payload.topic,
                    link: payload.link,
                    description: payload.description,
                });

                setItems((prev) => [res.data, ...prev]);
                toast.success("Brochure created.");
            }

            setIsFormOpen(false);
            setEditing(null);
        } catch {
            toast.error("Failed to save brochure.");
        } finally {
            setBusyId(null);
        }
    }

    return (
        <div className="min-h-screen bg-gray-100 px-3 sm:px-4 md:px-6 py-6">
            <div className="w-full max-w-full mx-auto">
                {/* Header */}
                <div className="mb-5 sm:mb-7">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
                                Brochures
                            </h1>
                            <p className="mt-2 text-sm sm:text-base text-slate-600">
                                Manage brochure topics and links.
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={fetchBrochures}
                                className={[
                                    "h-11 px-4 rounded-xl border border-slate-200 bg-white shadow-sm",
                                    "text-slate-700 font-semibold hover:text-slate-900 hover:border-slate-300",
                                    "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                                ].join(" ")}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <RefreshCw className="h-4 w-4" />
                                    Refresh
                                </span>
                            </button>

                            <button
                                type="button"
                                onClick={openCreate}
                                className={[
                                    "h-11 px-4 rounded-xl bg-slate-900 text-white font-semibold shadow-sm",
                                    "hover:bg-slate-800 transition-colors",
                                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                                ].join(" ")}
                            >
                                <span className="inline-flex items-center gap-2">
                                    <Plus className="h-4 w-4" />
                                    Add brochure
                                </span>
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 h-px w-full bg-slate-200" />
                </div>

                {/* Controls */}
                <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                    <div className="p-4 sm:p-5">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div className="min-w-0">
                                <div className="flex items-center gap-2">
                                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
                                        <FileText className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <h2 className="text-lg sm:text-xl font-bold text-slate-900">Library</h2>
                                        <p className="mt-1 text-sm text-slate-600">
                                            {filtered.length} of {items.length} shown
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full sm:w-[420px]">
                                <div className="relative">
                                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                                        <Search className="h-5 w-5" />
                                    </span>

                                    <input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Search topic, link, description…"
                                        className={[
                                            "h-11 w-full rounded-xl border border-slate-200 bg-white pl-10 pr-10",
                                            "text-slate-900 placeholder:text-slate-400 shadow-sm",
                                            "focus:outline-none focus:ring-2 focus:ring-slate-400/70 focus:border-slate-300",
                                        ].join(" ")}
                                    />

                                    {query ? (
                                        <button
                                            type="button"
                                            onClick={() => setQuery("")}
                                            className={[
                                                "absolute right-2 top-1/2 -translate-y-1/2",
                                                "h-8 w-8 rounded-lg border border-slate-200 bg-white shadow-sm",
                                                "text-slate-600 hover:text-slate-900 hover:border-slate-300",
                                                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                                            ].join(" ")}
                                            aria-label="Clear search"
                                        >
                                            <X className="mx-auto h-4 w-4" />
                                        </button>
                                    ) : null}
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 h-px w-full bg-slate-200" />

                        {/* Table (desktop) */}
                        <div className="mt-4 hidden md:block">
                            <div className="rounded-2xl border border-slate-200 overflow-hidden">
                                <table className="min-w-full bg-white">
                                    <thead className="bg-slate-50">
                                        <tr>
                                            <Th>Topic</Th>
                                            <Th>Link</Th>
                                            <Th>Description</Th>
                                            <Th>Created</Th>
                                            <Th className="text-right">Actions</Th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-200">
                                        {loading ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-10 text-center text-slate-600">
                                                    Loading…
                                                </td>
                                            </tr>
                                        ) : filtered.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="px-4 py-10 text-center text-slate-600">
                                                    No brochures found.
                                                </td>
                                            </tr>
                                        ) : (
                                            filtered.map((b) => (
                                                <tr key={b.brochure_id} className="hover:bg-slate-50/60">
                                                    <td className="px-4 py-3.5 text-sm font-semibold text-slate-900 align-top">
                                                        <div className="max-w-[260px] truncate">{b.topic}</div>
                                                    </td>

                                                    <td className="px-4 py-3.5 text-sm text-slate-700 align-top">
                                                        <a
                                                            href={b.link}
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="max-w-[380px] block truncate font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                                        >
                                                            {b.link}
                                                        </a>
                                                    </td>

                                                    <td className="px-4 py-3.5 text-sm text-slate-700 align-top">
                                                        <div className="max-w-[420px] line-clamp-2">
                                                            {b.description || <span className="text-slate-400">No description</span>}
                                                        </div>
                                                    </td>

                                                    <td className="px-4 py-3.5 text-sm text-slate-700 align-top">
                                                        {formatDateTime(b.created_at)}
                                                    </td>

                                                    <td className="px-4 py-3.5 align-top">
                                                        <div className="flex items-center justify-end gap-2">
                                                            <IconButton
                                                                title="Edit"
                                                                onClick={() => openEdit(b)}
                                                                disabled={busyId === b.brochure_id}
                                                            >
                                                                <Pencil className="h-4 w-4" />
                                                            </IconButton>

                                                            <IconButton
                                                                title="Delete"
                                                                onClick={() => openDelete(b)}
                                                                disabled={busyId === b.brochure_id}
                                                                danger
                                                            >
                                                                <Trash2 className="h-4 w-4" />
                                                            </IconButton>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Cards (mobile) */}
                        <div className="mt-4 md:hidden">
                            {loading ? (
                                <div className="py-10 text-center text-slate-600">Loading…</div>
                            ) : filtered.length === 0 ? (
                                <div className="py-10 text-center text-slate-600">No brochures found.</div>
                            ) : (
                                <div className="grid grid-cols-1 gap-3">
                                    {filtered.map((b) => (
                                        <div
                                            key={b.brochure_id}
                                            className="rounded-2xl border border-slate-200 bg-white shadow-sm p-4"
                                        >
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="min-w-0">
                                                    <div className="text-base font-bold text-slate-900 truncate">{b.topic}</div>
                                                    <div className="mt-2 flex flex-wrap items-center gap-2">
                                                        <span className="text-xs text-slate-600">{formatDateTime(b.created_at)}</span>
                                                    </div>
                                                </div>

                                                <div className="flex items-center gap-2">
                                                    <IconButton
                                                        title="Edit"
                                                        onClick={() => openEdit(b)}
                                                        disabled={busyId === b.brochure_id}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </IconButton>

                                                    <IconButton
                                                        title="Delete"
                                                        onClick={() => openDelete(b)}
                                                        disabled={busyId === b.brochure_id}
                                                        danger
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </IconButton>
                                                </div>
                                            </div>

                                            <div className="mt-3">
                                                <a
                                                    href={b.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="block truncate font-semibold text-slate-900 underline decoration-slate-300 underline-offset-4 hover:decoration-slate-500"
                                                >
                                                    {b.link}
                                                </a>
                                            </div>

                                            <div className="mt-3 text-sm text-slate-700">
                                                {b.description || <span className="text-slate-400">No description</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <BrochureFormModal
                    open={isFormOpen}
                    onClose={() => {
                        setIsFormOpen(false);
                        setEditing(null);
                    }}
                    onSave={handleSave}
                    saving={busyId === "new" || (editing?.brochure_id && busyId === editing.brochure_id)}
                    initial={editing}
                />

                <ConfirmDeleteModal
                    open={isDeleteOpen}
                    onClose={() => {
                        setIsDeleteOpen(false);
                        setDeleting(null);
                    }}
                    onConfirm={handleDeleteConfirm}
                    confirming={deleting?.brochure_id && busyId === deleting.brochure_id}
                    title="Delete brochure"
                    description={
                        deleting
                            ? `This will permanently delete “${deleting.topic}”.`
                            : "This will permanently delete the brochure."
                    }
                />
            </div>
        </div>
    );
}

/* ----------------------------- UI Pieces ----------------------------- */

function Th({ children, className = "" }) {
    return (
        <th
            className={[
                "px-4 py-3 text-left text-xs font-bold uppercase tracking-wide text-slate-600",
                className,
            ].join(" ")}
        >
            {children}
        </th>
    );
}

function IconButton({ children, onClick, title, disabled, danger }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            disabled={disabled}
            className={[
                "h-9 w-9 rounded-full border shadow-sm grid place-items-center transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                disabled ? "opacity-60 cursor-not-allowed" : "",
                danger
                    ? "border-rose-200 bg-white text-rose-700 hover:border-rose-300 hover:text-rose-800"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:text-slate-900",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

function BrochureFormModal({ open, onClose, onSave, saving, initial }) {
    const isEdit = Boolean(initial?.brochure_id);

    const [topic, setTopic] = useState("");
    const [link, setLink] = useState("");
    const [description, setDescription] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        setError(null);
        setTopic(initial?.topic || "");
        setLink(initial?.link || "");
        setDescription(initial?.description || "");
    }, [open, initial]);

    function validate() {
        const t = topic.trim();
        const l = link.trim();
        if (!t) return "Topic is required.";
        if (!l) return "Link is required.";
        return null;
    }

    function submit(e) {
        e.preventDefault();
        const err = validate();
        if (err) {
            setError(err);
            toast.error(err);
            return;
        }
        onSave({
            brochure_id: initial?.brochure_id,
            topic: topic.trim(),
            link: link.trim(),
            description: description?.trim() ? description.trim() : "",
        });
    }

    return (
        <ModalShell open={open} onClose={onClose} title={isEdit ? "Edit brochure" : "Add brochure"}>
            <form onSubmit={submit} className="p-5 sm:p-6">
                {error ? (
                    <div className="mb-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
                        <div className="font-semibold">Fix required</div>
                        <div className="mt-1">{error}</div>
                    </div>
                ) : null}

                <div className="grid grid-cols-1 gap-4">
                    <Field label="Topic" required>
                        <input
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            className={[
                                "h-11 w-full rounded-xl border border-slate-200 bg-white px-3",
                                "text-slate-900 placeholder:text-slate-400 shadow-sm",
                                "focus:outline-none focus:ring-2 focus:ring-slate-400/70 focus:border-slate-300",
                            ].join(" ")}
                            placeholder="e.g. TITAN Sliding Door System"
                        />
                    </Field>

                    <Field label="Link" required>
                        <input
                            value={link}
                            onChange={(e) => setLink(e.target.value)}
                            className={[
                                "h-11 w-full rounded-xl border border-slate-200 bg-white px-3",
                                "text-slate-900 placeholder:text-slate-400 shadow-sm",
                                "focus:outline-none focus:ring-2 focus:ring-slate-400/70 focus:border-slate-300",
                            ].join(" ")}
                            placeholder="https://…"
                        />
                    </Field>

                    <Field label="Description">
                        <textarea
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            rows={4}
                            className={[
                                "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5",
                                "text-slate-900 placeholder:text-slate-400 shadow-sm",
                                "focus:outline-none focus:ring-2 focus:ring-slate-400/70 focus:border-slate-300",
                            ].join(" ")}
                            placeholder="Optional notes…"
                        />
                    </Field>
                </div>

                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className={[
                            "h-11 px-4 rounded-xl border border-slate-200 bg-white shadow-sm",
                            "text-slate-700 font-semibold hover:text-slate-900 hover:border-slate-300 transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                        ].join(" ")}
                    >
                        Cancel
                    </button>

                    <button
                        type="submit"
                        disabled={saving}
                        className={[
                            "h-11 px-4 rounded-xl bg-slate-900 text-white font-semibold shadow-sm",
                            "hover:bg-slate-800 transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                            saving ? "opacity-70 cursor-not-allowed" : "",
                        ].join(" ")}
                    >
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Create brochure"}
                    </button>
                </div>
            </form>
        </ModalShell>
    );
}

function ConfirmDeleteModal({ open, onClose, onConfirm, confirming, title, description }) {
    return (
        <ModalShell open={open} onClose={onClose} title={title}>
            <div className="p-5 sm:p-6">
                <div className="flex items-start gap-4">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-600 text-white">
                        <AlertTriangle className="h-5 w-5" />
                    </div>

                    <div className="min-w-0">
                        <div className="text-sm sm:text-base text-slate-700">{description}</div>
                        <div className="mt-2 text-xs text-slate-500">This action cannot be undone.</div>
                    </div>
                </div>

                <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className={[
                            "h-11 px-4 rounded-xl border border-slate-200 bg-white shadow-sm",
                            "text-slate-700 font-semibold hover:text-slate-900 hover:border-slate-300 transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                        ].join(" ")}
                    >
                        Cancel
                    </button>

                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={confirming}
                        className={[
                            "h-11 px-4 rounded-xl bg-rose-600 text-white font-semibold shadow-sm",
                            "hover:bg-rose-700 transition-colors",
                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-300/70",
                            confirming ? "opacity-70 cursor-not-allowed" : "",
                        ].join(" ")}
                    >
                        {confirming ? "Deleting…" : "Delete"}
                    </button>
                </div>
            </div>
        </ModalShell>
    );
}

/**
 * Centred modals using fixed flex wrapper (robust against containing-block quirks).
 */
function ModalShell({ open, onClose, title, children }) {
    return (
        <AnimatePresence>
            {open ? (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    aria-modal="true"
                    role="dialog"
                >
                    <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />

                    <motion.div
                        className="relative w-full max-w-2xl"
                        initial={{ opacity: 0, y: 12, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.99 }}
                        transition={{ type: "spring", stiffness: 520, damping: 38 }}
                    >
                        <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                            <div className="px-5 py-4 sm:px-6 sm:py-5 border-b border-slate-200">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="text-lg sm:text-xl font-extrabold tracking-tight text-slate-900 truncate">
                                            {title}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className={[
                                            "h-9 w-9 rounded-full border border-slate-200 bg-white shadow-sm",
                                            "text-slate-600 hover:text-slate-900 hover:border-slate-300 transition-colors",
                                            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/70",
                                        ].join(" ")}
                                        aria-label="Close"
                                    >
                                        <XCircle className="mx-auto h-4 w-4" />
                                    </button>
                                </div>
                            </div>

                            {children}
                        </div>
                    </motion.div>
                </motion.div>
            ) : null}
        </AnimatePresence>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-900">
                <span>{label}</span>
                {required ? (
                    <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700">
                        Required
                    </span>
                ) : null}
            </div>
            <div className="mt-2">{children}</div>
        </label>
    );
}

function formatDateTime(value) {
    if (!value) return "—";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString("en-AU", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
}